
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */
import { assertIsFn, AddContext } from "@storybook/addon-docs";

import { Canvas, Meta, Story } from '@storybook/addon-docs';
// The '!!raw-loader!' syntax is a non-standard, Webpack-specific, syntax.
// See: https://github.com/webpack-contrib/raw-loader#examples
// For now, it seems likely Storybook is pretty tied to Webpack, therefore, we are
// okay with the following Webpack-specific raw loader syntax. It's better to leave
// the ESLint rule enabled globally, and only thoughtfully disable as needed (e.g.
// within a Storybook docs page and not within an actual component).
// This can be revisited in the future if Storybook no longer relies on Webpack.
// eslint-disable-next-line @cloudfour/import/no-webpack-loader-syntax
import stylesDemoSource from '!!raw-loader!./demo/styles.twig';
import stylesDemo from './demo/styles.twig';
// eslint-disable-next-line @cloudfour/import/no-webpack-loader-syntax
import iconButtonCustomDemoSource from '!!raw-loader!./demo/icon-button-custom-demo.twig';
import iconButtonCustomDemo from './demo/icon-button-custom-demo.twig';
// eslint-disable-next-line @cloudfour/import/no-webpack-loader-syntax
import slashedIconButtonCustomDemoSource from '!!raw-loader!./demo/slashed-icon-button-custom-demo.twig';
import slashedIconButtonCustomDemo from './demo/slashed-icon-button-custom-demo.twig';
import button from './button.twig';
const iconControlConfig = {
  options: [
    '',
    'bell',
    'magnifying-glass',
    'heart',
    'brands/github',
    'brands/twitter',
  ],
  type: { name: 'string' },
  control: {
    type: 'select',
  },
};
const buttonStory = (args) => {
  // Don't bother with the inline options if they don't exist or are defaults
  if (args.content_start_icon === '') {
    delete args.content_start_icon;
  }
  if (args.content_end_icon === '') {
    delete args.content_end_icon;
  }
  if (args.disabled === false) {
    delete args.disabled;
  }
  if (args.type === 'button') {
    delete args.type;
  }
  return button(args);
};


const layoutProps = {
  
};
const MDXLayout = "wrapper"
function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <Meta title="Components/Button" args={{
      type: 'button',
      disabled: false
    }} argTypes={{
      href: {
        type: {
          name: 'string'
        }
      },
      type: {
        options: ['button', 'submit'],
        type: {
          name: 'enum'
        },
        control: {
          type: 'inline-radio'
        }
      },
      disabled: {
        type: {
          name: 'boolean'
        }
      },
      content_start_icon: iconControlConfig,
      content_end_icon: iconControlConfig
    }} mdxType="Meta" />
    <h1>{`Button`}</h1>
    <p>{`The `}<inlineCode parentName="p">{`c-button`}</inlineCode>{` class may be applied to button and link elements. The button appears filled by default so that the actionable element is clearly distinguished from surrounding text.`}</p>
    <Canvas mdxType="Canvas">
  <Story name="Button Element" args={{
        label: 'Button',
        href: false
      }} mdxType="Story">
    {args => buttonStory(args)}
  </Story>
  <Story name="Link Element" args={{
        label: 'Link',
        tagName: 'a'
      }} mdxType="Story">
    {args => buttonStory(args)}
  </Story>
    </Canvas>
    <h2>{`Styles`}</h2>
    <p>{`Some buttons may not require as much emphasis as others. The `}<inlineCode parentName="p">{`c-button--secondary`}</inlineCode>{` and `}<inlineCode parentName="p">{`c-button--tertiary`}</inlineCode>{` modifiers reflect lesser importance with reduced contrast.`}</p>
    <Canvas mdxType="Canvas">
  <Story name="Styles" parameters={{
        docs: {
          source: {
            code: stylesDemoSource
          }
        }
      }} mdxType="Story">
    {args => stylesDemo(args)}
  </Story>
    </Canvas>
    <p>{`All the above visual styles will appear slightly different within `}<a parentName="p" {...{
        "href": "/docs/design-themes--dark"
      }}>{`our dark theme`}</a>{`. These versions retain their visual emphasis relative to one another while remaining visually distinct from form inputs.`}</p>
    <Canvas mdxType="Canvas">
  <Story name="Styles (Dark)" parameters={{
        theme: 't-dark',
        docs: {
          source: {
            code: stylesDemoSource
          }
        }
      }} mdxType="Story">
    {args => stylesDemo(args)}
  </Story>
    </Canvas>
    <h2>{`Button with icon`}</h2>
    <p>{`To add an icon, pass the name of any of the `}<a parentName="p" {...{
        "href": "/docs/design-icons--page"
      }}>{`available icons`}</a>{`
to the `}<inlineCode parentName="p">{`content_start_icon`}</inlineCode>{` or `}<inlineCode parentName="p">{`content_end_icon`}</inlineCode>{` properties.`}</p>
    <Canvas mdxType="Canvas">
  <Story name="Icon" args={{
        content_start_icon: 'bell',
        label: 'Get notifications'
      }} mdxType="Story">
    {args => buttonStory(args)}
  </Story>
    </Canvas>
    <h2>{`Custom icon`}</h2>
    <p>{`If you need a custom icon, you can pass an `}<inlineCode parentName="p">{`<svg>`}</inlineCode>{` element directly via the
`}<inlineCode parentName="p">{`content_start`}</inlineCode>{` or `}<inlineCode parentName="p">{`content_end`}</inlineCode>{` Button component block. This option is only
recommended in unique cases. Also note the usage of the `}<inlineCode parentName="p">{`c-icon`}</inlineCode>{`
CSS classes on the `}<inlineCode parentName="p">{`<svg>`}</inlineCode>{` element.`}</p>
    <p>{`The `}<inlineCode parentName="p">{`content_start`}</inlineCode>{`/`}<inlineCode parentName="p">{`content_end`}</inlineCode>{` blocks override the
`}<inlineCode parentName="p">{`content_start_icon`}</inlineCode>{`/`}<inlineCode parentName="p">{`content_end_icon`}</inlineCode>{` values.`}</p>
    <Canvas mdxType="Canvas">
  <Story name="Custom Icon" parameters={{
        docs: {
          source: {
            code: iconButtonCustomDemoSource
          }
        }
      }} mdxType="Story">
    {args => iconButtonCustomDemo(args)}
  </Story>
    </Canvas>
    <h2>{`States`}</h2>
    <p>{`Buttons with a `}<inlineCode parentName="p">{`disabled`}</inlineCode>{` attribute or with `}<inlineCode parentName="p">{`aria-disabled`}</inlineCode>{` set to `}<inlineCode parentName="p">{`'true'`}</inlineCode>{` will appear less prominent. (`}<a parentName="p" {...{
        "href": "https://css-tricks.com/making-disabled-buttons-more-inclusive/"
      }}>{`Which should you use?`}</a>{`)`}</p>
    <p>{`Buttons with the `}<inlineCode parentName="p">{`is-loading`}</inlineCode>{` class will display a spinner. This informs the user visually that the button is waiting for some sort of response.`}</p>
    <Canvas mdxType="Canvas">
  <Story name="Disabled" args={{
        label: 'Disabled',
        disabled: true
      }} mdxType="Story">
    {args => buttonStory(args)}
  </Story>
  <Story name="ARIA Disabled" args={{
        label: 'ARIA Disabled',
        aria_disabled: 'true'
      }} mdxType="Story">
    {args => buttonStory(args)}
  </Story>
  <Story name="Loading" args={{
        label: 'Loading',
        class: 'is-loading'
      }} mdxType="Story">
    {args => buttonStory(args)}
  </Story>
    </Canvas>
    <h3>{`Icon with slash`}</h3>
    <p>{`To add a slash across the icon(s), use the `}<inlineCode parentName="p">{`is-slashed`}</inlineCode>{` state class.`}</p>
    <p>{`If no `}<inlineCode parentName="p">{`content_start_icon`}</inlineCode>{`/`}<inlineCode parentName="p">{`content_end_icon`}</inlineCode>{` or `}<inlineCode parentName="p">{`content_start`}</inlineCode>{`/`}<inlineCode parentName="p">{`content_end`}</inlineCode>{` block content is passed in, the `}<inlineCode parentName="p">{`'bell'`}</inlineCode>{` icon will be used by default as the `}<inlineCode parentName="p">{`content_start_icon`}</inlineCode>{` value.`}</p>
    <p>{`You can override the `}<inlineCode parentName="p">{`content_start_icon`}</inlineCode>{` value to a different icon.`}</p>
    <Canvas mdxType="Canvas">
  <Story name="Slashed Icon" args={{
        class: 'is-slashed'
      }} mdxType="Story">
    {args => buttonStory(args)}
  </Story>
  <Story name="Secondary Button with Slashed Icon" args={{
        class: 'c-button--secondary is-slashed'
      }} mdxType="Story">
    {args => buttonStory(args)}
  </Story>
  <Story name="Tertiary Button with Slashed Icon" args={{
        class: 'c-button--tertiary is-slashed'
      }} mdxType="Story">
    {args => buttonStory(args)}
  </Story>
  <Story name="Slashed Custom Icon" parameters={{
        docs: {
          source: {
            code: slashedIconButtonCustomDemoSource
          }
        }
      }} mdxType="Story">
    {args => slashedIconButtonCustomDemo(args)}
  </Story>
    </Canvas>
    <h2>{`Template Properties`}</h2>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`aria_disabled`}</inlineCode>{` (string, `}<inlineCode parentName="li">{`'true'`}</inlineCode>{`/`}<inlineCode parentName="li">{`'false'`}</inlineCode>{`): Sets the button's `}<inlineCode parentName="li">{`aria-disabled`}</inlineCode>{` attribute.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`aria_expanded`}</inlineCode>{` (string, `}<inlineCode parentName="li">{`'true'`}</inlineCode>{`/`}<inlineCode parentName="li">{`'false'`}</inlineCode>{`): Sets the button's `}<inlineCode parentName="li">{`aria-expanded`}</inlineCode>{` attribute.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`class`}</inlineCode>{` (string): Append a class to the root element.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`content_start_icon`}</inlineCode>{` (string): The name of the `}<a parentName="li" {...{
          "href": "/docs/design-icons--page"
        }}>{`icon`}</a>{` to render in the `}<inlineCode parentName="li">{`content_start`}</inlineCode>{` block.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`content_end_icon`}</inlineCode>{` (string): The name of the `}<a parentName="li" {...{
          "href": "/docs/design-icons--page"
        }}>{`icon`}</a>{` to render in the `}<inlineCode parentName="li">{`content_end`}</inlineCode>{` block.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`disabled`}</inlineCode>{` (boolean): Sets the button's `}<inlineCode parentName="li">{`disabled`}</inlineCode>{` attribute.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`href`}</inlineCode>{` (string): Converts the button to an anchor element and assigns the `}<inlineCode parentName="li">{`href`}</inlineCode>{` value. Defaults to `}<inlineCode parentName="li">{`#`}</inlineCode>{` if `}<inlineCode parentName="li">{`a`}</inlineCode>{` is passed as the `}<inlineCode parentName="li">{`tag_name`}</inlineCode>{`.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`label`}</inlineCode>{` (string, default `}<inlineCode parentName="li">{`'Hello world'`}</inlineCode>{`): Sets the button's text content.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`tag_name`}</inlineCode>{` (string, default `}<inlineCode parentName="li">{`'button'`}</inlineCode>{`): The tag for the button. Uses a `}<inlineCode parentName="li">{`button`}</inlineCode>{` by default, an `}<inlineCode parentName="li">{`a`}</inlineCode>{` when an `}<inlineCode parentName="li">{`href`}</inlineCode>{` value is passed.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`type`}</inlineCode>{` (string): Sets the button's `}<inlineCode parentName="li">{`type`}</inlineCode>{` value. If none is passed the browser will default to `}<inlineCode parentName="li">{`submit`}</inlineCode>{` or `}<inlineCode parentName="li">{`button`}</inlineCode>{` depending on whether the button is in a form.`}</li>
    </ul>
    <h2>{`Template Blocks`}</h2>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`content_start`}</inlineCode>{`: Block to add content before the Button `}<inlineCode parentName="li">{`content`}</inlineCode>{` block.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`content`}</inlineCode>{`: Block to add Button elements.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`content_end`}</inlineCode>{`: Block to add content after the Button `}<inlineCode parentName="li">{`content`}</inlineCode>{` block.`}</li>
    </ul>
    <h2>{`See Also`}</h2>
    <p>{`See the `}<a parentName="p" {...{
        "href": "/docs/objects-button-group--default-story"
      }}>{`Button Group object`}</a>{` to align a group of related Button components.`}</p>
    </MDXLayout>;
}

;
MDXContent.isMDXComponent = true;

export const buttonElement = (args => buttonStory(args));
buttonElement.storyName = 'Button Element';
buttonElement.args = {
  label: 'Button',
  href: false
};
buttonElement.parameters = { storySource: { source: 'args => buttonStory(args)' } };

export const linkElement = (args => buttonStory(args));
linkElement.storyName = 'Link Element';
linkElement.args = {
  label: 'Link',
  tagName: 'a'
};
linkElement.parameters = { storySource: { source: 'args => buttonStory(args)' } };

export const styles = (args => stylesDemo(args));
styles.storyName = 'Styles';
styles.parameters = { storySource: { source: 'args => stylesDemo(args)' }, ...{
  docs: {
    source: {
      code: stylesDemoSource
    }
  }
} };

export const stylesDark = (args => stylesDemo(args));
stylesDark.storyName = 'Styles (Dark)';
stylesDark.parameters = { storySource: { source: 'args => stylesDemo(args)' }, ...{
  theme: 't-dark',
  docs: {
    source: {
      code: stylesDemoSource
    }
  }
} };

export const icon = (args => buttonStory(args));
icon.storyName = 'Icon';
icon.args = {
  content_start_icon: 'bell',
  label: 'Get notifications'
};
icon.parameters = { storySource: { source: 'args => buttonStory(args)' } };

export const customIcon = (args => iconButtonCustomDemo(args));
customIcon.storyName = 'Custom Icon';
customIcon.parameters = { storySource: { source: 'args => iconButtonCustomDemo(args)' }, ...{
  docs: {
    source: {
      code: iconButtonCustomDemoSource
    }
  }
} };

export const disabled = (args => buttonStory(args));
disabled.storyName = 'Disabled';
disabled.args = {
  label: 'Disabled',
  disabled: true
};
disabled.parameters = { storySource: { source: 'args => buttonStory(args)' } };

export const ariaDisabled = (args => buttonStory(args));
ariaDisabled.storyName = 'ARIA Disabled';
ariaDisabled.args = {
  label: 'ARIA Disabled',
  aria_disabled: 'true'
};
ariaDisabled.parameters = { storySource: { source: 'args => buttonStory(args)' } };

export const loading = (args => buttonStory(args));
loading.storyName = 'Loading';
loading.args = {
  label: 'Loading',
  class: 'is-loading'
};
loading.parameters = { storySource: { source: 'args => buttonStory(args)' } };

export const slashedIcon = (args => buttonStory(args));
slashedIcon.storyName = 'Slashed Icon';
slashedIcon.args = {
  class: 'is-slashed'
};
slashedIcon.parameters = { storySource: { source: 'args => buttonStory(args)' } };

export const secondaryButtonWithSlashedIcon = (args => buttonStory(args));
secondaryButtonWithSlashedIcon.storyName = 'Secondary Button with Slashed Icon';
secondaryButtonWithSlashedIcon.args = {
  class: 'c-button--secondary is-slashed'
};
secondaryButtonWithSlashedIcon.parameters = { storySource: { source: 'args => buttonStory(args)' } };

export const tertiaryButtonWithSlashedIcon = (args => buttonStory(args));
tertiaryButtonWithSlashedIcon.storyName = 'Tertiary Button with Slashed Icon';
tertiaryButtonWithSlashedIcon.args = {
  class: 'c-button--tertiary is-slashed'
};
tertiaryButtonWithSlashedIcon.parameters = { storySource: { source: 'args => buttonStory(args)' } };

export const slashedCustomIcon = (args => slashedIconButtonCustomDemo(args));
slashedCustomIcon.storyName = 'Slashed Custom Icon';
slashedCustomIcon.parameters = { storySource: { source: 'args => slashedIconButtonCustomDemo(args)' }, ...{
  docs: {
    source: {
      code: slashedIconButtonCustomDemoSource
    }
  }
} };

const componentMeta = { title: 'Components/Button', args: {
  type: 'button',
  disabled: false
}, argTypes: {
  href: {
    type: {
      name: 'string'
    }
  },
  type: {
    options: ['button', 'submit'],
    type: {
      name: 'enum'
    },
    control: {
      type: 'inline-radio'
    }
  },
  disabled: {
    type: {
      name: 'boolean'
    }
  },
  content_start_icon: iconControlConfig,
  content_end_icon: iconControlConfig
}, includeStories: ["buttonElement","linkElement","styles","stylesDark","icon","customIcon","disabled","ariaDisabled","loading","slashedIcon","secondaryButtonWithSlashedIcon","tertiaryButtonWithSlashedIcon","slashedCustomIcon"],  };

const mdxStoryNameToKey = {"Button Element":"buttonElement","Link Element":"linkElement","Styles":"styles","Styles (Dark)":"stylesDark","Icon":"icon","Custom Icon":"customIcon","Disabled":"disabled","ARIA Disabled":"ariaDisabled","Loading":"loading","Slashed Icon":"slashedIcon","Secondary Button with Slashed Icon":"secondaryButtonWithSlashedIcon","Tertiary Button with Slashed Icon":"tertiaryButtonWithSlashedIcon","Slashed Custom Icon":"slashedCustomIcon"};

componentMeta.parameters = componentMeta.parameters || {};
componentMeta.parameters.docs = {
  ...(componentMeta.parameters.docs || {}),
  page: () => <AddContext mdxStoryNameToKey={mdxStoryNameToKey} mdxComponentAnnotations={componentMeta}><MDXContent /></AddContext>,
};

export default componentMeta;